export type DemoPoint = readonly [number, number]

export const DEMO_TOWER_MIN_SEPARATION = 46

export interface DemoPlacementArea {
  x: number
  y: number
  radiusX: number
  radiusY: number
}

export interface PathFollowerState {
  pathIndex: number
  x: number
  y: number
  complete: boolean
}

export interface DemoLayout {
  isMobile: boolean
  topInset: number
  bottomInset: number
  controlY: number
  controlHeight: number
}

export function getDemoLayout(width: number): DemoLayout {
  const isMobile = width > 1000
  return isMobile
    ? { isMobile, topInset: 165, bottomInset: 65, controlY: 120, controlHeight: 70 }
    : { isMobile, topInset: 105, bottomInset: 65, controlY: 34, controlHeight: 58 }
}

export function getDemoControlPositions(width: number) {
  return getDemoLayout(width).isMobile
    ? {
        menu: 72, menuWidth: 110, place: 215, placeWidth: 150,
        spawn: 430, spawnWidth: 230, bearLabel: 610, bearMinus: 690,
        bearValue: 744, bearPlus: 798, spaLabel: 895, spaMinus: 970,
        spaValue: 1024, spaPlus: 1078, remove: 1220, removeWidth: 150,
        styleLabel: 1470, stepWidth: 48,
      }
    : {
        menu: 48, menuWidth: 68, place: 139, placeWidth: 98,
        spawn: 270, spawnWidth: 142, bearLabel: 379, bearMinus: 427,
        bearValue: 460, bearPlus: 493, spaLabel: 554, spaMinus: 597,
        spaValue: 630, spaPlus: 663, remove: 744, removeWidth: 96,
        styleLabel: width - 70, stepWidth: 32,
      }
}

export function getDemoTowerRange(width: number): number {
  return getDemoLayout(width).isMobile ? 180 : 120
}

export function getDemoPlacementArea(width: number, height: number): DemoPlacementArea {
  const layout = getDemoLayout(width)
  const top = layout.topInset
  const bottom = height - layout.bottomInset
  const cy = (top + bottom) / 2
  const range = getDemoTowerRange(width)
  return {
    x: width / 2,
    y: cy,
    radiusX: range * 0.25,
    radiusY: range * 0.21,
  }
}

export function buildDemoPath(width: number, height: number): DemoPoint[] {
  const layout = getDemoLayout(width)
  const top = layout.topInset
  const bottom = height - layout.bottomInset
  const cx = width / 2
  const cy = (top + bottom) / 2
  const range = getDemoTowerRange(width)
  const rx = range * 0.55
  const ry = range * 0.58
  const left: DemoPoint = [cx - rx, cy]

  // Enter at the loop's left tangent, complete one broad loop, then overlap
  // the upper half before leaving at the right tangent. The center remains open.
  return [
    [-40, cy],
    left,
    [cx - rx * 0.92, cy - ry * 0.58],
    [cx - rx * 0.55, cy - ry],
    [cx, cy - ry],
    [cx + rx * 0.55, cy - ry],
    [cx + rx * 0.92, cy - ry * 0.58],
    [cx + rx, cy],
    [cx + rx * 0.92, cy + ry * 0.58],
    [cx + rx * 0.55, cy + ry],
    [cx, cy + ry],
    [cx - rx * 0.55, cy + ry],
    [cx - rx * 0.92, cy + ry * 0.58],
    left,
    [cx - rx * 0.92, cy - ry * 0.58],
    [cx - rx * 0.55, cy - ry],
    [cx, cy - ry],
    [cx + rx * 0.55, cy - ry],
    [cx + rx * 0.92, cy - ry * 0.58],
    [cx + rx, cy],
    [width + 40, cy],
  ]
}

export function isInsideDemoPlacementArea(x: number, y: number, width: number, height: number): boolean {
  const area = getDemoPlacementArea(width, height)
  const nx = (x - area.x) / area.radiusX
  const ny = (y - area.y) / area.radiusY
  return nx * nx + ny * ny <= 1
}

export function advancePathFollower(
  state: PathFollowerState,
  path: readonly DemoPoint[],
  distance: number,
): PathFollowerState {
  if (state.complete || path.length === 0) return { ...state }
  if (path.length === 1) {
    return { pathIndex: 0, x: path[0][0], y: path[0][1], complete: true }
  }

  let pathIndex = Math.min(Math.max(0, state.pathIndex), path.length - 1)
  let x = state.x
  let y = state.y
  let remaining = Math.max(0, distance)

  while (pathIndex < path.length - 1) {
    const [tx, ty] = path[pathIndex + 1]
    const dx = tx - x
    const dy = ty - y
    const segmentRemaining = Math.hypot(dx, dy)

    if (segmentRemaining === 0) {
      pathIndex++
      continue
    }

    if (remaining < segmentRemaining) {
      const ratio = remaining / segmentRemaining
      return {
        pathIndex,
        x: x + dx * ratio,
        y: y + dy * ratio,
        complete: false,
      }
    }

    x = tx
    y = ty
    remaining -= segmentRemaining
    pathIndex++
  }

  return { pathIndex, x, y, complete: true }
}
